// client/src/components/pages/RouteIntelligence/PreloadDashboard.tsx (converted from .js, ST-00229)
//
// ANL-9: adding, editing, deleting and running monitors need route_intelligence.manage; without
// it those controls are hidden (viewing details and acknowledging stay).
// ANL-15: Run / Run All start a background job on the server and the page polls it to
// completion (useMonitorRuns), instead of one request that outlived the 30 s client timeout.
import { useState, useEffect, useCallback } from 'react';
import { Activity, Plus, PlayCircle, Search, Filter } from 'lucide-react';
import api from '../../../services/api';
import { useAuth } from '../../../context/AuthContext';
import { getPreloadState, preloadStates } from './preloadConfig';
import PreloadMonitorCard from './PreloadMonitorCard';
import PreloadMonitorSettingsModal from './PreloadMonitorSettingsModal';
import PreloadDetailModal from './PreloadDetailModal';
import ThemedSelect from '../../common/ThemedSelect';
import { describeFailure, useMonitorRuns, type RunToastType } from './useMonitorRuns';
import type {
  MonitorApiError,
  MonitorRunOutcome,
  PreloadDashboardMonitor,
} from '../../../types/clientPages/routeIntelligence';

type Monitor = PreloadDashboardMonitor;

interface Toast {
  message: string;
  type: RunToastType;
}

/** The toast for one finished manual run (same wording as before ST-00229). */
const describeRun = (monitor: Monitor, d: MonitorRunOutcome): Toast =>
  d.success
    ? {
        message: `${monitor.grouping_name}: ${d.daysWithData} days processed, ${d.preloadCount} preloads`,
        type: d.isBreaching ? 'warning' : 'success',
      }
    : describeFailure(monitor.grouping_name, d);

const PreloadDashboard = () => {
  const { hasPermission } = useAuth();
  const canManage = hasPermission('route_intelligence.manage');
  const [monitors, setMonitors] = useState<Monitor[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterState, setFilterState] = useState('all');
  const [toast, setToast] = useState<Toast | null>(null);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [editMonitor, setEditMonitor] = useState<Monitor | null>(null);
  const [showDetailModal, setShowDetailModal] = useState<Monitor | null>(null);

  const fetchDashboard = useCallback(async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      setError(null);
      const res = await api.preloadEvaluation.getDashboard();
      setMonitors((res.data as Monitor[]) || []);
    } catch (err) {
      setError((err as MonitorApiError).response?.data?.error || 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);
  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(t);
  }, [toast]);
  const showToast = useCallback(
    (message: string, type: RunToastType = 'success') => setToast({ message, type }),
    []
  );
  const refresh = useCallback(() => {
    void fetchDashboard(true);
  }, [fetchDashboard]);
  const {
    runningIds: runningMonitorIds,
    runOne,
    runAll,
  } = useMonitorRuns<Monitor>({
    api: api.preloadEvaluation,
    showToast,
    refresh,
    describeResult: describeRun,
  });

  const handleRunCheck = (monitor: Monitor) => {
    void runOne(monitor);
  };

  const handleRunAll = () => {
    void runAll(monitors.filter((m) => m.is_active).map((m) => m.monitor_id));
  };

  const handleDeleteMonitor = async (monitorId: number) => {
    try {
      await api.preloadEvaluation.deleteMonitor(monitorId);
      showToast('Monitor deleted');
      fetchDashboard(true);
    } catch {
      showToast('Failed to delete', 'error');
    }
  };

  const handleSettingsSaved = () => {
    setShowSettingsModal(false);
    setEditMonitor(null);
    fetchDashboard(true);
    showToast(editMonitor ? 'Monitor updated' : 'Monitor created');
  };

  const handleEditMonitor = (m: Monitor) => {
    setEditMonitor(m);
    setShowSettingsModal(true);
  };

  const filteredMonitors = monitors.filter((m) => {
    if (searchQuery && !m.grouping_name?.toLowerCase().includes(searchQuery.toLowerCase()))
      return false;
    if (filterState !== 'all') {
      const s = getPreloadState(m.latest_preload_count, m.alert_threshold);
      if (filterState === 'normal' && s !== preloadStates.normal) return false;
      if (filterState === 'watch' && s !== preloadStates.watch) return false;
      if (filterState === 'alert' && s !== preloadStates.alert) return false;
    }
    return true;
  });

  const kpis = {
    total: monitors.length,
    normal: monitors.filter(
      (m) => getPreloadState(m.latest_preload_count, m.alert_threshold) === preloadStates.normal
    ).length,
    watch: monitors.filter(
      (m) => getPreloadState(m.latest_preload_count, m.alert_threshold) === preloadStates.watch
    ).length,
    alert: monitors.filter(
      (m) => getPreloadState(m.latest_preload_count, m.alert_threshold) === preloadStates.alert
    ).length,
  };

  return (
    <>
      {/* Actions (route_intelligence.manage, ANL-9) */}
      {canManage && (
        <div className="flex items-center justify-end gap-2 mb-5">
          <button
            onClick={handleRunAll}
            disabled={monitors.length === 0 || runningMonitorIds.size > 0}
            className="inline-flex items-center gap-1.5 h-8 px-3 text-xs font-medium text-primary-500 bg-teal-50 hover:bg-teal-100 border border-teal-200 rounded-md disabled:opacity-40 transition-colors"
          >
            <PlayCircle
              className={`w-3.5 h-3.5 ${runningMonitorIds.size > 0 ? 'animate-spin' : ''}`}
            />
            {runningMonitorIds.size > 0 ? 'Running...' : 'Run All'}
          </button>
          <button
            onClick={() => {
              setEditMonitor(null);
              setShowSettingsModal(true);
            }}
            className="inline-flex items-center gap-1.5 h-8 px-3 text-xs font-semibold text-white rounded-md transition-colors bg-primary-500"
          >
            <Plus className="w-3.5 h-3.5" /> Add Monitor
          </button>
        </div>
      )}

      {/* KPIs */}
      {!loading && monitors.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3 mb-5 sm:mb-6">
          {[
            {
              label: 'Total',
              value: kpis.total,
              color: 'var(--text-strong)',
              bg: 'bg-surface',
              border: 'border-gray-200',
              icon: Activity,
            },
            {
              label: 'Clear',
              value: kpis.normal,
              color: 'var(--accent)',
              bg: 'bg-teal-50',
              border: 'border-teal-200',
              icon: preloadStates.normal.icon,
            },
            {
              label: 'Watch',
              value: kpis.watch,
              color: 'var(--warning)',
              bg: 'bg-amber-50',
              border: 'border-amber-200',
              icon: preloadStates.watch.icon,
            },
            {
              label: 'Alert',
              value: kpis.alert,
              color: 'var(--danger)',
              bg: 'bg-red-50',
              border: 'border-red-200',
              icon: preloadStates.alert.icon,
            },
          ].map((kpi, i) => (
            <div
              key={i}
              className={`${kpi.bg} rounded-lg border ${kpi.border} px-3 sm:px-4 py-2.5 sm:py-3 flex items-center gap-2.5 sm:gap-3`}
            >
              <kpi.icon className="w-4 h-4 flex-shrink-0" style={{ color: kpi.color }} />
              <div>
                <div
                  className="text-base sm:text-lg font-bold tabular-nums"
                  style={{ color: kpi.color }}
                >
                  {kpi.value}
                </div>
                <div className="text-[10px] text-gray-400 uppercase tracking-wider">
                  {kpi.label}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Search / Filter */}
      {monitors.length > 0 && (
        <div className="flex items-center gap-2 mb-5">
          <div className="relative flex-1 sm:max-w-xs">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-300" />
            <input
              type="text"
              placeholder="Search routes..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-8 pr-3 h-8 bg-surface border border-gray-200 rounded-md text-xs text-gray-700 placeholder:text-gray-400 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20 focus:outline-none"
            />
          </div>
          <div className="relative">
            <Filter className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-300" />
            <ThemedSelect
              value={filterState}
              onChange={(e) => setFilterState(String(e.target.value))}
              className="pl-8 pr-6 h-8 bg-surface border border-gray-200 rounded-md text-xs text-gray-600 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20 focus:outline-none appearance-none"
            >
              <option value="all">All</option>
              <option value="normal">Clear</option>
              <option value="watch">Watch</option>
              <option value="alert">Alert</option>
            </ThemedSelect>
          </div>
        </div>
      )}

      {error && (
        <div className="mb-5 p-3 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-xs text-red-600">{error}</p>
          <button
            onClick={() => fetchDashboard()}
            className="mt-1 text-xs text-red-500 hover:text-red-700 underline"
          >
            Retry
          </button>
        </div>
      )}

      {loading && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-surface rounded-lg border border-gray-200 p-4 animate-pulse">
              <div className="h-4 bg-gray-100 rounded w-2/3 mb-3" />
              <div className="h-7 bg-gray-50 rounded w-1/3 mb-3" />
              <div className="h-3 bg-gray-50 rounded mb-3" />
              <div className="h-8 bg-gray-50 rounded" />
            </div>
          ))}
        </div>
      )}

      {!loading && filteredMonitors.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {filteredMonitors.map((m, idx) => (
            <PreloadMonitorCard
              key={m.monitor_id}
              monitor={m}
              onViewDetails={setShowDetailModal}
              onRunCheck={handleRunCheck}
              onEditMonitor={handleEditMonitor}
              onDeleteMonitor={handleDeleteMonitor}
              isRunning={runningMonitorIds.has(m.monitor_id)}
              canManage={canManage}
              delay={idx * 40}
            />
          ))}
        </div>
      )}

      {!loading && monitors.length === 0 && !error && (
        <div className="text-center py-16">
          <Activity className="w-10 h-10 mx-auto mb-3 text-primary-500" style={{ opacity: 0.3 }} />
          <h3 className="text-sm font-semibold text-slate-800 mb-1">
            No preload monitors configured
          </h3>
          <p className="text-xs text-gray-400 mb-5 max-w-sm mx-auto">
            Set up a monitor to detect cross-day preload deliveries on your route groupings.
          </p>
          {canManage && (
            <button
              onClick={() => {
                setEditMonitor(null);
                setShowSettingsModal(true);
              }}
              className="inline-flex items-center gap-2 h-9 px-4 text-xs font-semibold text-white rounded-md bg-primary-500"
            >
              <Plus className="w-3.5 h-3.5" /> Add Your First Monitor
            </button>
          )}
        </div>
      )}

      {!loading && monitors.length > 0 && filteredMonitors.length === 0 && (
        <div className="text-center py-10">
          <p className="text-xs text-gray-400">No monitors match your filter.</p>
        </div>
      )}

      {toast && (
        <div
          className={`fixed bottom-4 right-4 z-50 px-4 py-2.5 rounded-md shadow-lg text-xs font-medium ${
            toast.type === 'success'
              ? 'bg-primary-500 text-white'
              : toast.type === 'warning'
                ? 'bg-amber-500 text-white'
                : 'bg-red-500 text-white'
          }`}
        >
          {toast.message}
        </div>
      )}

      {showSettingsModal && (
        <PreloadMonitorSettingsModal
          editMonitor={editMonitor}
          onClose={() => {
            setShowSettingsModal(false);
            setEditMonitor(null);
          }}
          onSaved={handleSettingsSaved}
        />
      )}
      {showDetailModal && (
        <PreloadDetailModal
          monitor={showDetailModal}
          onClose={() => setShowDetailModal(null)}
          onRefresh={() => fetchDashboard(true)}
        />
      )}
    </>
  );
};

export default PreloadDashboard;
